import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  deleteCompanyUploadedFile,
  deletePaymentPlatformFile,
  getCompanyUploadedFileSignedUrl,
  previewPaymentPlatformImport,
  registerPaymentPlatformFile,
  startPaymentPlatformImport,
} from "../api/companyApi";
import PaymentPlatformColumnMappingDialog from "./PaymentPlatformColumnMappingDialog";
import PaymentPlatformFileUploadDialog from "./PaymentPlatformFileUploadDialog";
import { cleanupCancelledPaymentPlatformUpload } from "../utils/cancelledPaymentPlatformUpload";

const PaymentPlatformQuickUploadFlow = forwardRef(function PaymentPlatformQuickUploadFlow(
  { tenantId },
  ref,
) {
  const fileInput = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [pendingImport, setPendingImport] = useState(null);
  const [busy, setBusy] = useState(false);

  const open = useCallback(() => {
    if (!tenantId || busy) return;
    if (fileInput.current) fileInput.current.value = "";
    fileInput.current?.click();
  }, [tenantId, busy]);

  useImperativeHandle(ref, () => ({ open }), [open]);

  const chooseFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length) setSelectedFiles(files);
  };

  const uploadFiles = async (labelledFiles) => {
    setBusy(true);
    const uploaded = [];
    try {
      for (const { file, labels } of labelledFiles) {
        const signed = await getCompanyUploadedFileSignedUrl(tenantId, {
          fileName: file.name,
          fileType: file.type || "application/octet-stream",
        });
        await axios.put(signed.data.uploadUrl, file, {
          headers: { "Content-Type": file.type || "application/octet-stream" },
        });
        const storageFile = {
          fileGCSKey: signed.data.fileGCSKey,
          fileName: signed.data.originalFileName || file.name,
        };
        uploaded.push(storageFile);
        const catalogFile = await registerPaymentPlatformFile(tenantId, {
          ...storageFile,
          labels,
          source: "dashboard_upload",
        });
        uploaded[uploaded.length - 1] = catalogFile;
      }
      const preview = await previewPaymentPlatformImport(tenantId, uploaded);
      setPendingImport({ files: uploaded, preview });
      setSelectedFiles([]);
      toast.success("Files added. Map columns to finish the import.");
    } catch (error) {
      if (uploaded.length) {
        await cleanupCancelledPaymentPlatformUpload({
          tenantId,
          files: uploaded,
          deleteCatalogFile: deletePaymentPlatformFile,
          deleteStorageFile: deleteCompanyUploadedFile,
        });
      }
      toast.error(error?.response?.data?.message || "File upload failed");
    } finally {
      setBusy(false);
    }
  };

  const applyMapping = async (columnMapping) => {
    if (!pendingImport?.files?.length) return;
    setBusy(true);
    try {
      const job = await startPaymentPlatformImport(
        tenantId,
        pendingImport.files,
        null,
        columnMapping,
        true,
      );
      setPendingImport(null);
      window.dispatchEvent(new CustomEvent("payment-platform-import-started", {
        detail: { tenantId, job },
      }));
      toast.success("Import started. You can continue working on this page.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not start import");
    } finally {
      setBusy(false);
    }
  };

  const cancelPendingImport = async () => {
    if (!pendingImport?.files?.length) {
      setPendingImport(null);
      return;
    }
    setBusy(true);
    try {
      const result = await cleanupCancelledPaymentPlatformUpload({
        tenantId,
        files: pendingImport.files,
        deleteCatalogFile: deletePaymentPlatformFile,
        deleteStorageFile: deleteCompanyUploadedFile,
      });
      if (result.failed.length) {
        setPendingImport((current) => ({ ...current, files: result.failed }));
        toast.error("Cancellation was incomplete. The remaining uploaded files were kept for retry.");
        return;
      }
      setPendingImport(null);
      toast.info("Upload cancelled. Uploaded files and staged data were removed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        hidden
        multiple
        ref={fileInput}
        aria-label="Choose payment platform data files"
        type="file"
        accept=".csv,.xls,.xlsx"
        onChange={chooseFiles}
      />
      <PaymentPlatformFileUploadDialog
        open={selectedFiles.length > 0}
        files={selectedFiles}
        busy={busy}
        onClose={() => setSelectedFiles([])}
        onUpload={uploadFiles}
      />
      <PaymentPlatformColumnMappingDialog
        open={Boolean(pendingImport)}
        preview={pendingImport?.preview}
        currentMapping={null}
        busy={busy}
        onClose={cancelPendingImport}
        onApply={applyMapping}
        onSaveDraft={null}
      />
    </>
  );
});

export default PaymentPlatformQuickUploadFlow;
